import React from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ArAgingDetailResponse } from "@common/types/reports/types";
import { ReportPresentationChrome } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { AccountingReportFilters } from "../types";
import {
    AccountingReportContext,
    useAccountingReportContextValue,
} from "../AccountingReport.context";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { AccountingReportFooter } from "../AccountingReportFooter";
import { useReportChrome } from "../useReportChrome";
import { ArAgingDetailTable } from "./ArAgingDetailTable";
import styles from "./ArAgingDetailTable.module.scss";

interface Props {
    data: ArAgingDetailResponse;
    filters: AccountingReportFilters;
    chrome: ReportPresentationChrome;
    numberFormat: NumberFormatValues;
    generationDate: Date;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    footer?: React.ReactNode;
}

export const ArAgingDetailContent: React.FC<Props> = ({
    data,
    filters,
    chrome,
    numberFormat,
    generationDate,
    expanded,
    onExpandedChange,
    footer,
}) => {
    const resolvedChrome = useReportChrome({ chrome, generationDate });
    const accountingReportContextValue = useAccountingReportContextValue(
        filters,
        numberFormat,
    );

    return (
        <Card stacked sharpEdges fullPage>
            <AccountingReportContext.Provider
                value={accountingReportContextValue}
            >
                <CardSection className={styles.header}>
                    <AccountingReportHeader chrome={resolvedChrome} />
                </CardSection>
                <ArAgingDetailTable
                    data={data}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                    footer={
                        <>
                            {footer}
                            <AccountingReportFooter chrome={resolvedChrome} />
                        </>
                    }
                />
            </AccountingReportContext.Provider>
        </Card>
    );
};
